import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, saveSession, SERVICES } from "@/lib/api";
import Navbar from "@/components/karmivo/Navbar";
import { HardHat } from "lucide-react";

export default function WorkerRegister() {
  const nav = useNavigate();
  const [f, setF] = useState({
    name: "", phone: "", email: "", password: "", address: "", city: "Jaipur",
    category: "daily_labour", skills: "", experience_years: 1,
    id_proof_type: "aadhaar", id_proof_number: "", emergency_contact: ""
  });
  const [loading, setLoading] = useState(false);
  const upd = (k, v) => setF(x => ({ ...x, [k]: v }));
  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...f,
        email: f.email?.trim() ? f.email.trim() : undefined,
        skills: f.skills.split(",").map(s=>s.trim()).filter(Boolean),
      };
      const { data } = await api.post(`/workers/apply`, payload);
      saveSession("worker", data);
      toast.success("Application submitted. KYC under review.");
      nav("/worker/dashboard");
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map(x => x.msg || JSON.stringify(x)).join(", ") : (typeof d === "string" ? d : "Could not submit");
      toast.error(msg);
    }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-slate-50"><Navbar/>
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 bg-orange-500 text-white rounded-xl flex items-center justify-center"><HardHat size={20}/></div>
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Join Karmivo as a Worker</h1>
            <p className="text-sm text-slate-500">Verified workers earn 80% of labour charges per job.</p>
          </div>
        </div>
        <Card className="p-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Full Name</Label><Input value={f.name} onChange={e => upd("name", e.target.value)} data-testid="w-name"/></div>
            <div><Label>Phone</Label><Input value={f.phone} onChange={e => upd("phone", e.target.value)} data-testid="w-phone"/></div>
            <div><Label>Email (optional)</Label><Input value={f.email} onChange={e => upd("email", e.target.value)} data-testid="w-email"/></div>
            <div><Label>Password</Label><Input type="password" value={f.password} onChange={e => upd("password", e.target.value)} data-testid="w-password"/></div>
            <div className="sm:col-span-2"><Label>Address</Label><Textarea value={f.address} onChange={e => upd("address", e.target.value)} data-testid="w-address"/></div>
            <div><Label>City</Label><Input value={f.city} onChange={e => upd("city", e.target.value)} data-testid="w-city"/></div>
            <div>
              <Label>Service Category</Label>
              <Select value={f.category} onValueChange={v => upd("category", v)}>
                <SelectTrigger data-testid="w-category"><SelectValue/></SelectTrigger>
                <SelectContent>{SERVICES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>Skills (comma separated)</Label><Input value={f.skills} onChange={e => upd("skills", e.target.value)} placeholder="Lifting, Packing" data-testid="w-skills"/></div>
            <div><Label>Experience (years)</Label><Input type="number" min="0" value={f.experience_years} onChange={e => upd("experience_years", parseInt(e.target.value || 0))} data-testid="w-exp"/></div>
            <div>
              <Label>ID Proof Type</Label>
              <Select value={f.id_proof_type} onValueChange={v => upd("id_proof_type", v)}>
                <SelectTrigger data-testid="w-id-type"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="aadhaar">Aadhaar</SelectItem>
                  <SelectItem value="pan">PAN</SelectItem>
                  <SelectItem value="voter">Voter ID</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>ID Proof Number</Label><Input value={f.id_proof_number} onChange={e => upd("id_proof_number", e.target.value)} data-testid="w-id-number"/></div>
            <div><Label>Emergency Contact</Label><Input value={f.emergency_contact} onChange={e => upd("emergency_contact", e.target.value)} data-testid="w-emergency"/></div>
          </div>
          <div className="mt-5 flex items-center justify-between">
            <p className="text-xs text-slate-500">By submitting, you agree to Karmivo's terms. Your KYC will be reviewed before you receive assignments.</p>
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={submit} disabled={loading} data-testid="w-submit-btn">Submit Application</Button>
          </div>
          <div className="mt-3 text-xs text-slate-500 text-center">Already a worker? <Link to="/worker/login" className="text-orange-600 font-semibold" data-testid="w-login-link">Log in</Link></div>
        </Card>
      </div>
    </div>
  );
}
